import React, { useEffect, useState, useContext, useRef } from "react";

import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import TextField from "@material-ui/core/TextField";
import useSettings from "../../hooks/useSettings";
import { toast } from 'react-toastify';
import { makeStyles } from "@material-ui/core/styles";
import { grey, blue } from "@material-ui/core/colors";
import OnlyForSuperUser from "../OnlyForSuperUser";
import useAuth from "../../hooks/useAuth.js/index.js";

import {
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Box,
  Paper,
  Divider,
  useTheme,
} from "@material-ui/core";

import { Colorize, AttachFile, Delete } from "@material-ui/icons";
import ColorPicker from "../ColorPicker";
import ColorModeContext from "../../layout/themeContext";
import api from "../../services/api";
import { getBackendUrl } from "../../config";

import defaultLogoLight from "../../assets/logo.png";
import defaultLogoDark from "../../assets/logo-black.png";
import defaultLogoFavicon from "../../assets/favicon.ico";
import ColorBoxModal from "../ColorBoxModal/index.js";
import SmtpSettings from "./SmtpSettings.js";
import EmailPersonalization from "./EmailPersonalization.js";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  fixedHeightPaper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    height: 240,
  },
  tab: {
    borderRadius: 4,
    width: "100%",
    "& .MuiTab-wrapper": {
      color: "#128c7e"
    },
    "& .MuiTabs-flexContainer": {
      justifyContent: "center"
    }


  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
    width: "100%",
  },
  cardAvatar: {
    fontSize: "55px",
    color: grey[500],
    backgroundColor: "#ffffff",
    width: theme.spacing(7),
    height: theme.spacing(7),
  },
  cardTitle: {
    fontSize: "18px",
    color: blue[700],
  },
  cardSubtitle: {
    color: grey[600],
    fontSize: "14px",
  },
  alignRight: {
    textAlign: "right",
  },
  fullWidth: {
    width: "100%",
  },
  selectContainer: {
    width: "100%",
    textAlign: "left",
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },

  uploadInput: {
    display: "none",
  },
  appLogoLightPreviewDiv: {
    backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') ? "#f5f5f5" : "white",
    padding: "10px",
    borderRadius: "12px",
    border: "2px solid #e0e0e0",
    textAlign: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    minHeight: "120px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },

  appLogoDarkPreviewDiv: {
    backgroundColor: "#424242",
    padding: "10px",
    borderRadius: "12px",
    border: "2px solid #666",
    textAlign: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    minHeight: "120px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },

  appLogoFaviconPreviewDiv: {
    padding: "15px",
    borderRadius: "12px",
    border: `2px solid ${theme.palette.divider}`,
    textAlign: "center",
    backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') 
      ? theme.palette.background.paper 
      : "#f8f9fa",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    minHeight: "120px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },

  browserTabSimulation: {
    backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') ? "#2a2a2a" : "#f1f3f4",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px 8px 0 0",
    padding: "8px 12px",
    fontSize: "12px",
    color: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') ? "#e0e0e0" : "#5f6368",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    maxWidth: "200px",
    margin: "0 auto 10px auto",
    fontFamily: "Arial, sans-serif",
  },

  faviconInTab: {
    width: "16px",
    height: "16px",
    borderRadius: "2px",
  },
  appLogoLightPreviewImg: {
    maxWidth: "120px",
    maxHeight: "60px",
    objectFit: "contain",
    content: "url(" + theme.calculatedLogoLight() + ")"
  },

  appLogoDarkPreviewImg: {
    maxWidth: "120px",
    maxHeight: "60px",
    objectFit: "contain",
    content: "url(" + theme.calculatedLogoDark() + ")"
  },

  appLogoFaviconPreviewImg: {
    width: "32px",
    height: "32px",
    objectFit: "contain",
    content: "url(" + ((theme.appLogoFavicon) ? theme.appLogoFavicon : "") + ")"
  },

  backgroundPreviewDiv: {
    padding: "20px",
    borderStyle: "solid",
    borderWidth: "1px",
    borderColor: "#424242",
    textAlign: "center",
    minHeight: "100px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: "bold",
    fontSize: "14px",
    marginTop: "10px",
  },
  backgroundSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "12px",
    backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
      ? theme.palette.background.paper
      : "#fafafa",
  },

  configSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },

  previewSection: {
    position: "sticky",
    top: theme.spacing(2),
  },

  backgroundPreviewContainer: {
    aspectRatio: "16/9",
    borderRadius: "12px",
    overflow: "hidden",
    border: `2px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  backgroundPreview: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: "bold",
    fontSize: "16px",
    textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
    position: "relative",
  },  loginSimulation: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    padding: "20px 16px 24px 16px",
    borderRadius: "8px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255,255,255,0.2)",
    width: "180px",
    textAlign: "center",
  },

  loginTitle: {
    color: "#333",
    marginBottom: "16px",
    fontSize: "13px",
    fontWeight: "bold",
    lineHeight: "1.2",
  },

  loginInput: {
    width: "100%",
    height: "32px",
    marginBottom: "12px",
    padding: "8px 10px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "11px",
    backgroundColor: "#fafafa",
    boxSizing: "border-box",
  },

  loginButton: {
    width: "100%",
    height: "36px",
    backgroundColor: "#25d366",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "bold",
    cursor: "pointer",
    marginTop: "8px",
  },

  uploadButton: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: "8px",
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
      ? theme.palette.background.default
      : "#f9f9f9",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
        ? theme.palette.background.paper
        : "#f0f0f0",
    },
  },

  instructionsBox: {
    backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
      ? `${theme.palette.primary.dark}20`
      : "#e3f2fd",
    padding: theme.spacing(2),
    borderRadius: "8px",
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },

  radioGroup: {
    gap: theme.spacing(1),
  },

  colorConfigRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  }
}));

export default function Whitelabel(props) {
  const { settings } = props;
  const classes = useStyles();
  const theme = useTheme();
  const [settingsLoaded, setSettingsLoaded] = useState({});

  const { getCurrentUserInfo } = useAuth();
  const [currentUser, setCurrentUser] = useState({});

  const { colorMode } = useContext(ColorModeContext);
  const [primaryColorLightModalOpen, setPrimaryColorLightModalOpen] = useState(false);
  const [primaryColorDarkModalOpen, setPrimaryColorDarkModalOpen] = useState(false);

  const logoLightInput = useRef(null);
  const logoDarkInput = useRef(null);
  const logoFaviconInput = useRef(null);
  const backgroundInput = useRef(null);
  const appNameInput = useRef(null);
  const [appName, setAppName] = useState(settingsLoaded.appName || "");  const [backgroundType, setBackgroundType] = useState("gradient"); // "gradient", "image", "color"
  const [backgroundColor, setBackgroundColor] = useState("#065183");
  const [backgroundColorModalOpen, setBackgroundColorModalOpen] = useState(false);
  const [gradientColor1, setGradientColor1] = useState("#065183");
  const [gradientColor2, setGradientColor2] = useState("#39ACE7");
  const [gradientColor1ModalOpen, setGradientColor1ModalOpen] = useState(false);  const [gradientColor2ModalOpen, setGradientColor2ModalOpen] = useState(false);
  const [imageRefresh, setImageRefresh] = useState(Date.now()); // Para forçar atualização da imagem

  const { update } = useSettings();

  const backendUrl = getBackendUrl();
  const normalizedBackendUrl = backendUrl ? backendUrl.replace(/\/+$/, "") : "";

  const buildPublicUrl = (fileName) => {
    if (!fileName) {
      return "";
    }

    if (normalizedBackendUrl) {
      return `${normalizedBackendUrl}/public/${fileName}`;
    }

    return `/public/${fileName}`;
  };

  function updateSettingsLoaded(key, value) {
    if (key === 'primaryColorLight' || key === 'primaryColorDark' || key === 'appName') {
      localStorage.setItem(key, value);
    };
    const newSettings = { ...settingsLoaded };
    newSettings[key] = value;
    setSettingsLoaded(newSettings);
  }
  useEffect(() => {
    getCurrentUserInfo().then(
      (u) => {
        setCurrentUser(u);
      }
    );

    if (Array.isArray(settings) && settings.length) {
      const primaryColorLight = settings.find((s) => s.key === "primaryColorLight")?.value;
      const primaryColorDark = settings.find((s) => s.key === "primaryColorDark")?.value;
      const appLogoLight = settings.find((s) => s.key === "appLogoLight")?.value;
      const appLogoDark = settings.find((s) => s.key === "appLogoDark")?.value;      const appLogoFavicon = settings.find((s) => s.key === "appLogoFavicon")?.value;
      const appName = settings.find((s) => s.key === "appName")?.value;
      const loginBackgroundType = settings.find((s) => s.key === "loginBackgroundType")?.value || "gradient";
      const loginBackgroundColor = settings.find((s) => s.key === "loginBackgroundColor")?.value || "#065183";
      const loginBackgroundImage = settings.find((s) => s.key === "loginBackgroundImage")?.value;
      const loginGradientColor1 = settings.find((s) => s.key === "loginGradientColor1")?.value || "#065183";
      const loginGradientColor2 = settings.find((s) => s.key === "loginGradientColor2")?.value || "#39ACE7";

      setAppName(appName || "");
      setBackgroundType(loginBackgroundType);
      setBackgroundColor(loginBackgroundColor);
      setGradientColor1(loginGradientColor1);
      setGradientColor2(loginGradientColor2);
      setSettingsLoaded({ 
        ...settingsLoaded, 
        primaryColorLight, 
        primaryColorDark, 
        appLogoLight, 
        appLogoDark, 
        appLogoFavicon, 
        appName,
        loginBackgroundType,
        loginBackgroundColor,
        loginBackgroundImage,
        loginGradientColor1,
        loginGradientColor2
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  async function handleSaveSetting(key, value) {

    await update({
      key,
      value,
    });
    updateSettingsLoaded(key, value);
    toast.success("Operação atualizada com sucesso.");
  }

  const uploadLogo = async (e, mode) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];
    const formData = new FormData();

    formData.append("typeArch", "logo");
    formData.append("mode", mode);
    formData.append("file", file);

    await api.post("/settings-whitelabel/logo", formData, {
      onUploadProgress: (event) => {
        let progress = Math.round(
          (event.loaded * 100) / event.total
        );
      },
    }).then((response) => {
      const fileName = response.data;
      updateSettingsLoaded(`appLogo${mode}`, fileName);

      const setter = colorMode?.[`setAppLogo${mode}`];
      if (typeof setter === "function") {
        setter(buildPublicUrl(fileName));
      }
    }).catch((err) => {
      console.error(
        `Houve um problema ao realizar o upload da imagem.`
      );
      console.log(err);
    });
  };
  const uploadBackground = async (e) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];
    
    // Validar se é JPEG
    if (!file.type.includes('jpeg') && !file.type.includes('jpg')) {
      toast.error("Por favor, selecione apenas arquivos JPEG (.jpg ou .jpeg)");
      return;
    }

    // Validar tamanho do arquivo (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("O arquivo deve ter no máximo 5MB");
      return;
    }

    const formData = new FormData();

    formData.append("typeArch", "background");
    formData.append("file", file);

    try {
      const response = await api.post("/settings-whitelabel/logo", formData, {
        onUploadProgress: (event) => {
          let progress = Math.round(
            (event.loaded * 100) / event.total
          );
          // Você pode adicionar um indicador de progresso aqui se quiser
        },
      });
        updateSettingsLoaded("loginBackgroundImage", response.data);
      setBackgroundType("image");
      handleSaveSetting("loginBackgroundType", "image");
      handleSaveSetting("loginBackgroundImage", response.data);
      setImageRefresh(Date.now()); // Força atualização da imagem no preview
      toast.success("Background atualizado com sucesso!");
    } catch (err) {
      console.error("Erro no upload:", err);
      toast.error("Houve um problema ao realizar o upload da imagem.");
    }
  };

  const handleBackgroundTypeChange = (type) => {
    setBackgroundType(type);
    handleSaveSetting("loginBackgroundType", type);
  };

  const handleBackgroundColorChange = (color) => {
    setBackgroundColor(color);
    handleSaveSetting("loginBackgroundColor", color);
  };

  const handleGradientColor1Change = (color) => {
    setGradientColor1(color);
    handleSaveSetting("loginGradientColor1", color);
  };

  const handleGradientColor2Change = (color) => {
    setGradientColor2(color);
    handleSaveSetting("loginGradientColor2", color);
  };  const renderBackgroundPreview = () => {
    let style = {};
    
    if (backgroundType === "image" && settingsLoaded.loginBackgroundImage) {
      style = {
        backgroundImage: `url(${getBackendUrl()}/public/${settingsLoaded.loginBackgroundImage}?t=${imageRefresh})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat"
      };
    } else if (backgroundType === "color") {
      style = {
        backgroundColor: backgroundColor
      };
    } else {
      // gradient - usando cores personalizadas
      style = {
        background: `linear-gradient(135deg, ${gradientColor1} 0%, ${gradientColor2} 100%)`
      };
    }

    return (
      <div className={classes.backgroundPreviewContainer}>
        <div className={classes.backgroundPreview} style={style}>
          {/* Simulação do formulário de login */}
          <div className={classes.loginSimulation}>
            <div className={classes.loginTitle}>
              {settingsLoaded.appName || "WhatTicket"}
            </div>
            <input 
              className={classes.loginInput} 
              placeholder="Email" 
              disabled 
              value="usuario@exemplo.com"
            />
            <input 
              className={classes.loginInput} 
              placeholder="Senha" 
              type="password" 
              disabled 
              value="••••••••"
            />
            <button className={classes.loginButton} disabled>
              Entrar
            </button>
          </div>
          
          {/* Indicador de preview */}
          <div style={{ 
            position: "absolute", 
            top: "10px", 
            right: "10px", 
            backgroundColor: "rgba(0,0,0,0.6)", 
            color: "white", 
            padding: "4px 8px", 
            borderRadius: "4px", 
            fontSize: "12px" 
          }}>
            Preview 16:9
          </div>
        </div>
      </div>
    );
  };
  return (
    <>
      <Grid spacing={3} container>
        <OnlyForSuperUser
          user={currentUser}
          yes={() => (
            <>
              {/* Seção de Configurações Gerais */}
              <Grid xs={12} item>
                <Paper className={classes.backgroundSection}>
                  <Typography variant="h5" gutterBottom style={{ fontWeight: "bold" }} color="textPrimary">
                    🎨 Configurações de Marca e Identidade Visual
                  </Typography>
                  
                  <Box className={classes.instructionsBox}>
                    <Typography variant="body2" style={{ color: theme.palette.primary.main, fontWeight: "500" }}>
                      📌 <strong>Instruções:</strong>
                    </Typography>
                    <Typography variant="body2" color="textPrimary" style={{ marginTop: "4px" }}>
                      • <strong>Nome do Sistema:</strong> Define o nome que aparece na tela de login e título da página<br/>
                      • <strong>Cores Primárias:</strong> Personalize as cores do tema claro e escuro da aplicação<br/>
                      • <strong>Logotipos:</strong> Faça upload dos logos para modo claro, escuro e favicon<br/>
                      • Recomendações: PNG/JPG para logos (máx. 2MB), ícone 32x32px para favicon
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>                    {/* Nome do Sistema e Cores do Tema */}
                    <Grid xs={12} item>
                      <Grid container spacing={3}>
                        {/* Nome do Sistema */}
                        <Grid xs={12} md={6} item>
                          <Typography variant="h6" gutterBottom color="textPrimary" style={{ marginBottom: "20px" }}>
                            📱 Nome do Sistema
                          </Typography>
                          <FormControl className={classes.selectContainer}>
                            <TextField
                              id="appname-field"
                              label="Nome do Sistema"
                              variant="outlined"
                              name="appName"
                              value={appName}
                              inputRef={appNameInput}
                              onChange={(e) => {
                                setAppName(e.target.value);
                              }}
                              onBlur={async (_) => {
                                await handleSaveSetting("appName", appName);
                                colorMode.setAppName(appName || "Plataforma");
                              }}
                              placeholder="Ex: Minha Empresa Chat"
                              helperText="Nome que aparece no login e título do navegador"
                            />
                          </FormControl>
                        </Grid>

                        {/* Cores do Tema */}
                        <Grid xs={12} md={6} item>
                          <Typography variant="h6" gutterBottom color="textPrimary" style={{ marginBottom: "20px" }}>
                            🎨 Cores do Tema
                          </Typography>
                          
                          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                            <div className={classes.colorConfigRow}>
                              <Typography variant="body2" style={{ minWidth: "120px" }}>
                                Tema Claro:
                              </Typography>
                              <div
                                style={{ 
                                  backgroundColor: settingsLoaded.primaryColorLight || "#2576d2",
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "8px",
                                  border: "2px solid #ddd",
                                  cursor: "pointer"
                                }}
                                onClick={() => setPrimaryColorLightModalOpen(true)}
                              />
                              <Typography variant="body2" color="textSecondary" style={{ fontSize: "12px" }}>
                                {settingsLoaded.primaryColorLight || "#2576d2"}
                              </Typography>
                            </div>

                            <div className={classes.colorConfigRow}>
                              <Typography variant="body2" style={{ minWidth: "120px" }}>
                                Tema Escuro:
                              </Typography>
                              <div
                                style={{ 
                                  backgroundColor: settingsLoaded.primaryColorDark || "#2576d2",
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "8px",
                                  border: "2px solid #ddd",
                                  cursor: "pointer"
                                }}
                                onClick={() => setPrimaryColorDarkModalOpen(true)}
                              />
                              <Typography variant="body2" color="textSecondary" style={{ fontSize: "12px" }}>
                                {settingsLoaded.primaryColorDark || "#2576d2"}
                              </Typography>
                            </div>
                          </div>                        </Grid>
                      </Grid>
                    </Grid>                          {/* Seção de Identidade Visual - Logotipos */}                    <div style={{ 
                      display: "flex", 
                      flexDirection: "column", 
                      gap: "16px", 
                      marginTop: "30px", 
                      maxWidth: "1200px", 
                      margin: "30px auto 0 auto",
                      backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
                        ? theme.palette.background.default
                        : "#fafafa",
                      borderRadius: "12px",
                      padding: "24px"
                    }}>
                        {/* Logo Tema Claro - 3 colunas: Descrição | Upload/Delete | Preview */}
                        <Grid container spacing={1} alignItems="center" justifyContent="center" style={{ maxWidth: "1000px", margin: "0 auto" }}>
                          {/* Coluna 1: Descrição */}                          <Grid xs={12} md={4} item>
                            <div style={{ textAlign: "center", padding: "8px" }}>
                              <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: "8px", fontWeight: "bold" }}>
                                🌞 Logo Tema Claro
                              </Typography>
                              <Typography variant="caption" color="textSecondary" style={{ fontSize: "11px", lineHeight: "1.4" }}>
                                Usado no cabeçalho quando o tema claro está ativo
                              </Typography>
                            </div>
                          </Grid>
                          
                          {/* Coluna 2: Upload e Delete */}
                          <Grid xs={12} md={4} item>
                            <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
                              <input
                                type="file"
                                id="upload-logo-light"
                                ref={logoLightInput}
                                className={classes.uploadInput}
                                accept="image/*"
                                onChange={(e) => uploadLogo(e, "Light")}
                              />
                              <label htmlFor="upload-logo-light">
                                <div className={classes.uploadButton}>
                                  <AttachFile style={{ marginRight: "8px", color: "#666" }} />
                                  <Typography variant="body2" color="textSecondary">
                                    {settingsLoaded.appLogoLight ? 
                                      "Trocar logo claro" : 
                                      "Carregar logo claro"
                                    }
                                  </Typography>
                                </div>
                              </label>
                              {settingsLoaded.appLogoLight && (
                                <Box style={{ textAlign: "center" }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      updateSettingsLoaded("appLogoLight", "");
                                      colorMode.setAppLogoLight("");
                                    }}
                                    style={{ color: "#f44336" }}
                                  >
                                    <Delete />
                                  </IconButton>
                                  <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                    Remover logo
                                  </Typography>
                                </Box>
                              )}
                            </div>
                          </Grid>                          {/* Coluna 3: Preview */}
                          <Grid xs={12} md={4} item>
                            <div className={classes.appLogoLightPreviewDiv} style={{ margin: 0, textAlign: "center", padding: "16px" }}>
                              <Typography variant="caption" style={{ color: "#666", marginBottom: "12px", display: "block", fontWeight: "bold" }}>
                                Preview no Cabeçalho
                              </Typography>
                              <img className={classes.appLogoLightPreviewImg} alt="light-logo-preview" />
                            </div>
                          </Grid>
                        </Grid>                        {/* Divisor */}
                        <Divider style={{ margin: "15px 0" }} />
                          {/* Logo Tema Escuro - 3 colunas: Descrição | Upload/Delete | Preview */}
                        <Grid container spacing={1} alignItems="center" justifyContent="center" style={{ maxWidth: "1000px", margin: "0 auto" }}>
                          {/* Coluna 1: Descrição */}
                          <Grid xs={12} md={4} item>
                            <div style={{ textAlign: "center", padding: "8px" }}>                              <Typography variant="subtitle2" style={{ color: "#666", marginBottom: "8px", fontWeight: "bold" }}>
                                🌙 Logo Tema Escuro
                              </Typography>
                              <Typography variant="caption" style={{ color: "#888", fontSize: "11px", lineHeight: "1.4" }}>
                                Usado no cabeçalho quando o tema escuro está ativo
                              </Typography>
                            </div>
                          </Grid>
                          
                          {/* Coluna 2: Upload e Delete */}
                          <Grid xs={12} md={4} item>
                            <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "8px" }}>
                              <input
                                type="file"
                                id="upload-logo-dark"
                                ref={logoDarkInput}
                                className={classes.uploadInput}
                                accept="image/*"
                                onChange={(e) => uploadLogo(e, "Dark")}
                              />
                              <label htmlFor="upload-logo-dark">
                                <div className={classes.uploadButton}>
                                  <AttachFile style={{ marginRight: "8px", color: "#666" }} />
                                  <Typography variant="body2" style={{ color: "#666" }}>
                                    {settingsLoaded.appLogoDark ? 
                                      "Trocar logo escuro" : 
                                      "Carregar logo escuro"
                                    }
                                  </Typography>
                                </div>
                              </label>
                              {settingsLoaded.appLogoDark && (
                                <Box style={{ textAlign: "center" }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      updateSettingsLoaded("appLogoDark", "");
                                      colorMode.setAppLogoDark("");
                                    }}
                                    style={{ color: "#f44336" }}
                                  >
                                    <Delete />
                                  </IconButton>
                                  <Typography variant="caption" style={{ color: "#666", display: "block" }}>
                                    Remover logo
                                  </Typography>
                                </Box>
                              )}
                            </div>
                          </Grid>                            {/* Coluna 3: Preview */}
                          <Grid xs={12} md={4} item>
                            <div className={classes.appLogoDarkPreviewDiv} style={{ margin: 0, textAlign: "center", padding: "8px" }}>
                              <Typography variant="caption" style={{ color: "#fff", marginBottom: "12px", display: "block", fontWeight: "bold" }}>
                                Preview no Cabeçalho
                              </Typography>
                              <img className={classes.appLogoDarkPreviewImg} alt="dark-logo-preview" />
                            </div>
                          </Grid>
                        </Grid>                        {/* Divisor */}
                        <Divider style={{ margin: "15px 0" }} />

                        {/* Favicon - 3 colunas: Descrição | Upload/Delete | Preview */}
                        <Grid container spacing={1} alignItems="center" justifyContent="center" style={{ maxWidth: "1000px", margin: "0 auto" }}>                          {/* Coluna 1: Descrição */}
                          <Grid xs={12} md={4} item>
                            <div style={{ textAlign: "center", padding: "8px" }}>
                              <Typography variant="subtitle2" style={{ color: "#666", marginBottom: "8px", fontWeight: "bold" }}>
                                🔖 Favicon (Ícone da Aba)
                              </Typography>
                              <Typography variant="caption" style={{ color: "#888", fontSize: "11px", lineHeight: "1.4" }}>
                                Aparece na aba do navegador e nos favoritos (16x16px recomendado)
                              </Typography>
                            </div>
                          </Grid>

                          {/* Coluna 2: Upload e Delete */}
                          <Grid xs={12} md={4} item>
                            <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "8px" }}>
                              <input
                                type="file"
                                id="upload-favicon"
                                ref={logoFaviconInput}
                                className={classes.uploadInput}
                                accept="image/*,.ico"
                                onChange={(e) => uploadLogo(e, "Favicon")}
                              />
                              <label htmlFor="upload-favicon">
                                <div className={classes.uploadButton}>
                                  <AttachFile style={{ marginRight: "8px", color: "#666" }} />
                                  <Typography variant="body2" style={{ color: "#666" }}>
                                    {settingsLoaded.appLogoFavicon ? 
                                      "Trocar favicon" : 
                                      "Carregar favicon"
                                    }
                                  </Typography>
                                </div>
                              </label>
                              {settingsLoaded.appLogoFavicon && (
                                <Box style={{ textAlign: "center" }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      updateSettingsLoaded("appLogoFavicon", "");
                                      colorMode.setAppLogoFavicon("");
                                    }}
                                    style={{ color: "#f44336" }}
                                  >
                                    <Delete />
                                  </IconButton>
                                  <Typography variant="caption" style={{ color: "#666", display: "block" }}>
                                    Remover favicon
                                  </Typography>
                                </Box>
                              )}
                            </div>
                          </Grid>
                            {/* Coluna 3: Preview */}
                          <Grid xs={12} md={4} item>
                            <div className={classes.appLogoFaviconPreviewDiv} style={{ margin: 0, textAlign: "center", padding: "16px" }}>
                              <Typography variant="caption" style={{ color: "#666", marginBottom: "12px", display: "block", fontWeight: "bold" }}>
                                Preview na Aba do Navegador
                              </Typography>
                              
                              {/* Simulação da aba do navegador */}
                              {(() => {
                                const fallbackFavicon = buildPublicUrl(settingsLoaded.appLogoFavicon) || defaultLogoFavicon;
                                const faviconPreviewSrc = theme.appLogoFavicon && theme.appLogoFavicon.trim()
                                  ? theme.appLogoFavicon
                                  : fallbackFavicon;

                                return (
                                  <div className={classes.browserTabSimulation}>
                                    <img
                                      className={classes.faviconInTab}
                                      src={faviconPreviewSrc}
                                      alt="favicon-in-tab"
                                      onError={(e) => {
                                        e.target.src = defaultLogoFavicon;
                                      }}
                                    />
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {settingsLoaded.appName || "WhatTicket"}
                                </span>
                                  </div>
                                );
                              })()}
                            </div>
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>
                </Paper>
              </Grid>

              {/* Seção de Background de Login */}
              <Grid xs={12} item>
                <Paper className={classes.backgroundSection}>
                  <Typography variant="h5" gutterBottom style={{ fontWeight: "bold" }} color="textPrimary">
                    🎨 Personalização do Background de Login
                  </Typography>
                  
                  <Box className={classes.instructionsBox}>
                    <Typography variant="body2" style={{ color: theme.palette.primary.main, fontWeight: "500" }}>
                      📌 <strong>Instruções:</strong>
                    </Typography>
                    <Typography variant="body2" color="textPrimary" style={{ marginTop: "4px" }}>
                      • <strong>Gradiente:</strong> Escolha duas cores para criar um fundo degradê personalizado<br/>
                      • <strong>Cor Sólida:</strong> Defina uma cor única para o fundo<br/>
                      • <strong>Imagem:</strong> Faça upload de uma imagem JPEG (será redimensionada automaticamente)<br/>
                      • O preview mostra como ficará na tela de login (proporção 16:9)
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid xs={12} md={6} item>
                      <div className={classes.configSection}>
                        <Typography variant="h6" gutterBottom style={{ color: "#555" }}>
                          Configurações
                        </Typography>
                        
                        <FormControl component="fieldset">
                          <RadioGroup
                            value={backgroundType}
                            onChange={(e) => handleBackgroundTypeChange(e.target.value)}
                            className={classes.radioGroup}
                          >
                            <FormControlLabel 
                              value="gradient" 
                              control={<Radio color="primary" />} 
                              label="🌈 Gradiente Personalizado" 
                            />
                            <FormControlLabel 
                              value="color" 
                              control={<Radio color="primary" />} 
                              label="🎨 Cor Sólida" 
                            />
                            <FormControlLabel 
                              value="image" 
                              control={<Radio color="primary" />} 
                              label="🖼️ Imagem Personalizada" 
                            />
                          </RadioGroup>
                        </FormControl>

                        <Divider style={{ margin: "16px 0" }} />

                        {backgroundType === "gradient" && (
                          <div>
                            <Typography variant="subtitle1" gutterBottom style={{ color: "#666" }}>
                              Cores do Gradiente
                            </Typography>
                            
                            <div className={classes.colorConfigRow}>
                              <Typography variant="body2" style={{ minWidth: "80px" }}>
                                Cor Inicial:
                              </Typography>
                              <div
                                style={{ 
                                  backgroundColor: gradientColor1,
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "8px",
                                  border: "2px solid #ddd",
                                  cursor: "pointer"
                                }}
                                onClick={() => setGradientColor1ModalOpen(true)}
                              />
                              <Typography variant="body2" style={{ fontSize: "12px", color: "#666" }}>
                                {gradientColor1}
                              </Typography>
                            </div>

                            <div className={classes.colorConfigRow}>
                              <Typography variant="body2" style={{ minWidth: "80px" }}>
                                Cor Final:
                              </Typography>
                              <div
                                style={{ 
                                  backgroundColor: gradientColor2,
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "8px",
                                  border: "2px solid #ddd",
                                  cursor: "pointer"
                                }}
                                onClick={() => setGradientColor2ModalOpen(true)}
                              />
                              <Typography variant="body2" style={{ fontSize: "12px", color: "#666" }}>
                                {gradientColor2}
                              </Typography>
                            </div>
                          </div>
                        )}

                        {backgroundType === "color" && (
                          <div>
                            <Typography variant="subtitle1" gutterBottom style={{ color: "#666" }}>
                              Cor do Fundo
                            </Typography>
                            
                            <div className={classes.colorConfigRow}>
                              <Typography variant="body2" style={{ minWidth: "80px" }}>
                                Cor:
                              </Typography>
                              <div
                                style={{ 
                                  backgroundColor: backgroundColor,
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "8px",
                                  border: "2px solid #ddd",
                                  cursor: "pointer"
                                }}
                                onClick={() => setBackgroundColorModalOpen(true)}
                              />
                              <Typography variant="body2" style={{ fontSize: "12px", color: "#666" }}>
                                {backgroundColor}
                              </Typography>
                            </div>
                          </div>
                        )}

                        {backgroundType === "image" && (
                          <div>
                            <Typography variant="subtitle1" gutterBottom style={{ color: "#666" }}>
                              Upload da Imagem
                            </Typography>
                            
                            <input
                              type="file"
                              id="upload-background-image"
                              ref={backgroundInput}
                              className={classes.uploadInput}
                              accept="image/jpeg,image/jpg"
                              onChange={uploadBackground}
                            />
                            <label htmlFor="upload-background-image">
                              <div className={classes.uploadButton}>
                                <AttachFile style={{ marginRight: "8px", color: "#666" }} />
                                <Typography variant="body2" style={{ color: "#666" }}>
                                  {settingsLoaded.loginBackgroundImage ? 
                                    "Clique para trocar a imagem" : 
                                    "Clique para selecionar uma imagem JPEG"
                                  }
                                </Typography>
                              </div>
                            </label>
                            
                            {settingsLoaded.loginBackgroundImage && (
                              <Box style={{ marginTop: "8px", textAlign: "center" }}>                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    handleSaveSetting("loginBackgroundImage", "");
                                    updateSettingsLoaded("loginBackgroundImage", "");
                                    setImageRefresh(Date.now()); // Força atualização do preview
                                    toast.success("Imagem removida com sucesso!");
                                  }}
                                  style={{ color: "#f44336" }}
                                >
                                  <Delete />
                                </IconButton>
                                <Typography variant="caption" style={{ color: "#666" }}>
                                  Remover imagem
                                </Typography>
                              </Box>
                            )}
                          </div>
                        )}
                      </div>
                    </Grid>

                    <Grid xs={12} md={6} item>
                      <div className={classes.previewSection}>
                        <Typography variant="h6" gutterBottom style={{ color: "#555" }}>
                          Preview
                        </Typography>
                        {renderBackgroundPreview()}
                        <Typography variant="caption" style={{ color: "#666", textAlign: "center", display: "block" }}>
                          Este é o preview de como ficará o fundo da tela de login                        </Typography>
                      </div>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Seção de Configurações SMTP */}
              <Grid xs={12} item>
                <SmtpSettings />
              </Grid>

              {/* Seção de Personalização de Email (para todas as empresas) */}
              <Grid xs={12} item>
                <EmailPersonalization />
              </Grid>

              {/* Modais de cores */}
              <ColorBoxModal
                open={primaryColorLightModalOpen}
                handleClose={() => setPrimaryColorLightModalOpen(false)}
                onChange={(color) => {
                  handleSaveSetting("primaryColorLight", `#${color.hex}`);
                  colorMode.setPrimaryColorLight(`#${color.hex}`);
                }}
                currentColor={settingsLoaded.primaryColorLight}
              />
              
              <ColorBoxModal
                open={primaryColorDarkModalOpen}
                handleClose={() => setPrimaryColorDarkModalOpen(false)}
                onChange={(color) => {
                  handleSaveSetting("primaryColorDark", `#${color.hex}`);
                  colorMode.setPrimaryColorDark(`#${color.hex}`);
                }}
                currentColor={settingsLoaded.primaryColorDark}
              />
              
              <ColorBoxModal
                open={backgroundColorModalOpen}
                handleClose={() => setBackgroundColorModalOpen(false)}
                onChange={(color) => {
                  handleBackgroundColorChange(`#${color.hex}`);
                }}
                currentColor={backgroundColor}
              />
              
              <ColorBoxModal
                open={gradientColor1ModalOpen}
                handleClose={() => setGradientColor1ModalOpen(false)}
                onChange={(color) => {
                  handleGradientColor1Change(`#${color.hex}`);
                }}
                currentColor={gradientColor1}
              />
                <ColorBoxModal
                open={gradientColor2ModalOpen}
                handleClose={() => setGradientColor2ModalOpen(false)}
                onChange={(color) => {
                  handleGradientColor2Change(`#${color.hex}`);
                }}                currentColor={gradientColor2}
              />

            </>
          )}
        />
      </Grid>
    </>
  );
}
